/* eslint no-console: off */
import { FluxChart as Charts } from 'app/components/charts/chart';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { reactStrapStyles } from 'app/shared/util/form-utils';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { Button, Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { getEntities as getCities } from '../city/city.reducer';
import { getEntities as getDeviceMetrics } from '../device-reported-metric/device-reported-metric.reducer';
import { getEntities as getDevices, reset as resetDevices } from '../device/device.reducer';
import FilterSelector from './filterSelector';
import { TimeRange } from './timeRange';
import {
  ColorSchemeOptions,
  composeWidgetPayload,
  DeviceType,
  DisplayType,
  mapWidgetToFormValues,
  metrics,
  Widget,
  WidgetDefinition,
  YAxisOptions,
} from './utils/utils';

const initialValues = {
  widgetName: '',
  visualizationDisplay: null,
  metric: [],
  singleMetric: null,
  operation: 'SUM',
  queries: [],
  widgetOption: 'br',
  controller: null,
  colorScheme: null,
  tenant: '',
  inventoryType: null,
  nodes: null,
  city: null,
  deviceStatus: 'ALL',
};

type WidgetProps = {
  open: boolean;
  onComplete: (config: WidgetDefinition) => void;
  onCancel: () => void;
  widget?: Widget;
  timeRange: TimeRange;
};

const WidgetModal = ({ onComplete, widget, open, onCancel, timeRange }: WidgetProps) => {
  const organizations = useAppSelector(state => state.metric.organizations);
  const deviceList = useAppSelector(state => state.device.entities);
  const deviceLoading = useAppSelector(state => state.device.loading);
  const cities = useAppSelector(state => state.city.entities);
  const loadingCities = useAppSelector(state => state.city.loading);
  const [selectedFilter, setSelectedFilter] = useState({ label: 'Serial Number', value: 'serialNumber' });
  const applicationUser = useAppSelector(state => state.authentication?.internalUser);

  const [showCitiesMenu, setShowCitiesMenu] = useState(false);
  const [isCitiesFocued, setIsCitiesFocused] = useState(false);

  useEffect(() => {
    if (cities?.length > 0 && isCitiesFocued) {
      setShowCitiesMenu(true);
    }
  }, [cities?.length]);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
    trigger,
    clearErrors,
  } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const watchedValues = watch();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getDeviceMetrics({}));
  }, [dispatch]);

  const debouncedCitiesDispatch = useCallback(
    debounce(value => {
      dispatch(
        getCities({
          page: 0,
          size: 20,
          sort: 'name,asc',
          filterUrl: 'name.contains=' + value,
        }),
      );
    }, 500), // 500 ms debounce
    [dispatch],
  );

  const debouncedDevicesDispatch = useCallback(
    debounce(value => {
      const params = ['userId.specified=true', `master.equals=${watchedValues.widgetOption === 'br'}`];

      if (watchedValues.city) {
        params.push(`cityId.equals=${watchedValues.city.value}`);
      }
      if (watchedValues.deviceStatus && watchedValues.deviceStatus !== "ALL") {
        params.push(`state.equals=${watchedValues.deviceStatus}`);
      }
      params.push(`${selectedFilter.value}.contains=${value}`);

      dispatch(
        getDevices({
          page: 0,
          size: 10000000,
          sort: 'name,asc',
          filterUrl: params.join('&'),
        }),
      );
    }, 500), // 500 ms debounce
    [dispatch, selectedFilter.value, watchedValues.widgetOption, watchedValues.city, watchedValues.deviceStatus],
  );

  const defaultValues = useMemo(() => {
    if (widget) {
      return mapWidgetToFormValues(widget.definition, organizations);
    }
    return initialValues;
  }, [widget, organizations]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmitForm = values => {
    const payload = composeWidgetPayload(values, applicationUser?.organization);
    onComplete(payload);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleSave = async () => {
    const isFormValid = await trigger();
    if (isFormValid) {
      handleSubmit(onSubmitForm)();
    }
  };

  const chartValues = useMemo(() => {
    return {
      metric: watchedValues.metric,
      operation: watchedValues.operation,
      nodes: watchedValues.nodes,
      city: watchedValues.city,
      widgetOption: watchedValues.widgetOption,
      visualizationDisplay: watchedValues.visualizationDisplay,
      colorScheme: watchedValues.colorScheme,
      deviceStatus: watchedValues.deviceStatus
    };
  }, [
    watchedValues.metric,
    watchedValues.operation,
    watchedValues.nodes,
    watchedValues.city,
    watchedValues.widgetOption,
    watchedValues.visualizationDisplay,
    watchedValues.colorScheme,
    watchedValues.deviceStatus,
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Modal isOpen={open} className="widgetModal">
        <ModalHeader>
          <span>{widget ? `Edit Widget` : 'Add Widget'}</span>
        </ModalHeader>
        <ModalBody className="widgetModalBody">
          {(watchedValues.metric?.length > 0 || watchedValues.singleMetric || watchedValues.queries?.length > 0) &&
          watchedValues.visualizationDisplay ? (
            <DisplayChart timeRange={timeRange} values={chartValues} organizations={organizations} />
          ) : (
            <></>
          )}

          <Row style={{ marginTop: '10px' }}>
            <Col md={6}>
              <Controller
                name="widgetName"
                control={control}
                rules={{ required: 'Widget name is required' }}
                render={({ field, fieldState }) => (
                  <>
                    <Label>Widget Name</Label>
                    <Input
                      isInValid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      value={field.value}
                      name="widgetName"
                      label="Widget Name"
                      onChange={field.onChange}
                    />
                    <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>
                  </>
                )}
              />
            </Col>
            {watchedValues?.visualizationDisplay?.value !== 'map' && (
              <Col md={6}>
                <Controller
                  name="colorScheme"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div>
                      <Label>Color Scheme</Label>
                      <ReactSelect
                        {...field}
                        options={ColorSchemeOptions?.map(type => ({
                          label: type.name,
                          value: type.key,
                        }))}
                        styles={reactStrapStyles({ error: !!fieldState.error, width: '100%' })}
                        placeholder="Select Color Scheme"
                      />
                      {fieldState.error && <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>}
                    </div>
                  )}
                />
              </Col>
            )}
          </Row>

          <Row style={{ marginTop: '10px' }}>
            <Col md={6}>
              <Controller
                name="widgetOption"
                control={control}
                render={({ field }) => (
                  <FormGroup>
                    <Label>Widget Option</Label>
                    <div className="d-flex">
                      <FormGroup check inline style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="widgetOption"
                            value="br"
                            checked={field.value === 'br'}
                            onChange={e => {
                              field.onChange(e.target.value);
                              setValue('nodes', null);
                              dispatch(resetDevices());
                            }}
                          />
                          Border Router
                        </Label>
                      </FormGroup>
                      <FormGroup check inline style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="widgetOption"
                            value="nodes"
                            checked={field.value === 'nodes'}
                            // disabled={isSubTenant}
                            onChange={e => {
                              field.onChange(e.target.value);
                              setValue('nodes', null);
                              dispatch(resetDevices());
                            }}
                          />
                          Nodes
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Controller
                name="city"
                control={control}
                rules={{ required: watchedValues?.visualizationDisplay?.value !== 'map' ? false : 'City is required' }}
                render={({ field, fieldState }) => (
                  <div>
                    <label>City</label>
                    <ReactSelect
                      {...field}
                      options={cities?.map(c => {
                        return {
                          label: c.name + ', ' + c.county + ', ' + c.state,
                          value: c.id,
                        };
                      })}
                      isClearable={true}
                      onChange={option => {
                        field.onChange(option);
                        setValue('nodes', null);
                        dispatch(resetDevices());
                        setShowCitiesMenu(false);
                      }}
                      onInputChange={val => {
                        if (!!val) {
                          debouncedCitiesDispatch(val);
                          if (cities?.length > 1) {
                            setShowCitiesMenu(true);
                          }
                        }
                      }}
                      isLoading={loadingCities}
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      menuIsOpen={showCitiesMenu}
                      styles={reactStrapStyles({ error: !!fieldState.error, width: '100%' })}
                      placeholder={'Search... ' + (watchedValues?.visualizationDisplay?.value !== 'map' ? '(Default: All)' : '')}
                      onBlur={() => {
                        setShowCitiesMenu(false);
                        setIsCitiesFocused(false);
                      }}
                      onFocus={() => {
                        setIsCitiesFocused(true);
                      }}
                    />
                    {fieldState.error && <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>}
                  </div>
                )}
              />
            </Col>
            <Col md={6}>
              <Controller
                name="deviceStatus"
                control={control}
                render={({ field }) => (
                  <FormGroup>
                    <Label>Device Status</Label>
                    <div className="d-flex">
                      <FormGroup check inline style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="deviceStatus"
                            value="ALL"
                            checked={field.value === 'ALL'}
                            onChange={e => {
                              field.onChange(e.target.value);
                              setValue('nodes', null);
                              dispatch(resetDevices());
                            }}
                          />
                          All
                        </Label>
                      </FormGroup>
                      <FormGroup check inline style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="deviceStatus"
                            value="ONLINE"
                            checked={field.value === 'ONLINE'}
                            onChange={e => {
                              field.onChange(e.target.value);
                              setValue('nodes', null);
                              dispatch(resetDevices());
                            }}
                          />
                          Online
                        </Label>
                      </FormGroup>
                      <FormGroup check inline style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="deviceStatus"
                            value="OFFLINE"
                            checked={field.value === 'OFFLINE'}
                            onChange={e => {
                              field.onChange(e.target.value);
                              setValue('nodes', null);
                              dispatch(resetDevices());
                            }}
                          />
                          Offline
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                )}
              />
            </Col>
          </Row>

          <NodesFields
            control={control}
            deviceList={deviceList}
            watchedValues={watchedValues}
            debouncedDevicesDispatch={debouncedDevicesDispatch}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            isDeviceLoading={deviceLoading}
          />

          <MetricFields control={control} setValue={setValue} watchedValues={watchedValues} clearErrors={clearErrors} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={handleCancel}>
            <span>Cancel</span>
          </Button>
          <Button color="primary" type="button" onClick={handleSave}>
            <span>{widget ? 'Save' : 'Add Widget'}</span>
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

type DisplayChartProps = {
  values: {
    metric?: { value: string }[];
    operation?: any;
    nodes?: { value: string }[];
    city?: any;
    deviceStatus?: string;
    widgetOption?: string;
    visualizationDisplay?: { value: string } | null;
    colorScheme?: { value: string } | null;
  };
  organizations: any; // refine this later if you know the exact shape
  timeRange: TimeRange;
};

const DisplayChart = React.memo(({ values, organizations, timeRange }: DisplayChartProps) => {
  return (
    <Row style={{ marginLeft: '5px', marginRight: '5px', minHeight: '300px' }}>
      <Col
        md={12}
        className="widget-charts"
        style={{
          minHeight: '100%',
          border: '1px solid #dbdbdb',
          borderRadius: '6px',
        }}
      >
        <Charts
          colorScheme={values.colorScheme?.value}
          timeRange={timeRange}
          dataQuery={{
            metric: values?.metric?.map(m => m.value),
            operation: values?.operation,
            queries: values?.nodes?.map(n => n.value),
            city: values?.city,
            deviceStatus: values?.deviceStatus
          }}
          widgetOption={values.widgetOption}
          type={values?.visualizationDisplay?.value}
          preview={true}
        />
      </Col>
    </Row>
  );
});

const NodesFields = ({
  control,
  deviceList,
  watchedValues,
  debouncedDevicesDispatch,
  selectedFilter,
  setSelectedFilter,
  isDeviceLoading,
}) => {
  const devices = deviceList?.map(device => ({
    label: device.serialNumber,
    value: device.serialNumber,
  }));

  const [menuOpen, setMenuOpen] = useState(false);
  const [isNodesFocused, setIsNodesFocused] = useState(false);

  useEffect(() => {
    if (devices?.length > 0 && isNodesFocused) {
      setMenuOpen(true);
    }
  }, [devices?.length]);

  return (
    <>
      <Row>
        <Col md={12}>
          <Label>{DeviceType[watchedValues.widgetOption]}</Label>
          <FilterSelector selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} />
          <br />
          <Controller
            name="nodes"
            control={control}
            render={({ field, fieldState }) => (
              <ReactSelect
                {...field}
                isMulti
                options={devices}
                onInputChange={value => {
                  if (!!value) {
                    debouncedDevicesDispatch(value);
                    if (devices?.length > 1) {
                      setMenuOpen(true);
                    }
                  }
                }}
                styles={reactStrapStyles({ error: !!fieldState.error, width: '100%' })}
                placeholder={`Search By ${selectedFilter.label}... (Default: All)`}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                menuIsOpen={menuOpen}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                isLoading={isDeviceLoading}
                onChange={option => {
                  field.onChange(option);
                }}
                value={field.value}
                onBlur={() => {
                  setMenuOpen(false);
                  setIsNodesFocused(false);
                }}
                onFocus={() => {
                  setIsNodesFocused(true);
                }}
                filterOption={null}
              />
            )}
          />
        </Col>
      </Row>
    </>
  );
};

const MetricFields = ({ control, setValue, watchedValues, clearErrors }) => {
  const fixedMap = [
    { label: 'rs_lat', value: 'rs_lat', isFixed: true },
    { label: 'rs_long', value: 'rs_long', isFixed: true },
  ];

  return (
    <>
      <Row>
        <Col md={12}>
          <Label>Metrics</Label>
          <Controller
            name="metric"
            control={control}
            rules={{ required: 'Metrics is required' }}
            render={({ field, fieldState }) => (
              <>
                <ReactSelect
                  {...field}
                  isMulti
                  options={metrics}
                  styles={reactStrapStyles({ error: !!fieldState.error, width: '100%' })}
                  placeholder="Metrics"
                  getOptionLabel={option => option.label}
                  getOptionValue={option => option.value}
                  onChange={option => {
                    if (watchedValues?.visualizationDisplay?.value === 'map') {
                      const merged = [...option, ...fixedMap.filter(fixed => !option.some(o => o.value === fixed.value))];
                      field.onChange(merged);
                    } else {
                      field.onChange(option);
                    }
                  }}
                  value={field.value}
                />
                <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Controller
            name="visualizationDisplay"
            control={control}
            rules={{ required: 'Visualization display is required' }}
            render={({ field, fieldState }) => (
              <div>
                <Label>Display Type</Label>
                <ReactSelect
                  {...field}
                  options={DisplayType?.map(type => ({
                    label: type.name,
                    value: type.key,
                  }))}
                  onChange={option => {
                    field.onChange(option);
                    clearErrors();
                    if (option?.value === 'map') {
                      setValue('metric', [
                        ...watchedValues?.metric?.filter(m => !fixedMap.map(f => f.value).includes(m.value)),
                        ...fixedMap,
                      ]);
                    } else {
                      setValue('metric', [...watchedValues?.metric?.filter(m => ![...fixedMap.map(f => f.value)].includes(m.value))]);
                    }
                  }}
                  styles={reactStrapStyles({ error: !!fieldState.error, width: '100%' })}
                  placeholder="Select Display"
                />
                {fieldState.error && <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>}
              </div>
            )}
          />
        </Col>
      </Row>
      {watchedValues?.visualizationDisplay?.value !== 'map' && (
        <Row>
          <Col md={12}>
            <Controller
              name="operation"
              control={control}
              render={({ field, fieldState }) => (
                <FormGroup>
                  <Label>Y Axis</Label>
                  <div className="d-flex flex-wrap">
                    {YAxisOptions.map(option => (
                      <FormGroup check inline key={option.value} style={{ fontSize: '16px' }}>
                        <Label check>
                          <Input
                            type="radio"
                            name="operation"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={e => {
                              field.onChange(e.target.value);
                              if (e.target.value === 'ANOMALY' || e.target.value === 'PREDICTION') {
                                setValue(
                                  'singleMetric',
                                  watchedValues.singleMetric ? watchedValues.singleMetric : watchedValues.metric?.[0] ?? null,
                                );
                                setValue('metric', []);
                              } else {
                                setValue('metric', watchedValues.singleMetric ? [watchedValues.singleMetric] : watchedValues.metric ?? []);
                                setValue('singleMetric', null);
                              }
                            }}
                          />
                          {option.label}
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                  {fieldState.error && <span className="form-error">{fieldState.error.message}</span>}
                </FormGroup>
              )}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export const AddWidgetModal = WidgetModal;
